import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { Box, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { Table } from "@vilocnv/allsetra-core";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportEmptyState from "components/common/reports/ReportEmptyState";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import WorkingHoursFilterForm from "components/forms/Reports/WorkingHoursFilterForm";
import WorkingHoursDateRowCard from "components/cards/ReportCards/WorkingHoursDateRowCard/WorkingHoursDateRowCard";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useReportInactiveObjects
} from "hooks";
import { selectAccountKeysState } from "app/data/selectors";
import {
  generateReportThunk,
  getAllKeysByAccountThunk,
  resetReports,
  resetReportsFilterData,
  setReportsDateValues,
  setReportsFilterData
} from "app/features";
import {
  convertToNormalTime,
  convertDatesForFilterPayload,
  reportTransformTimeForAPI,
  getStandardizedReportDates,
  formattedWorkingHours,
  reportsWorkingHoursFormInitialValues
} from "app/data/helpers";
import {
  getResetFilterFormValues,
  buildReportFilterPayload
} from "app/data/helpers/ReportHelpers";
import { WORKING_HOURS_FIRST_LAYER_COLUMNS } from "app/data/constants";
import { useTranslation } from "react-i18next";
import moment from "moment";

const WorkingHours: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const [openModal, setOpenModal] = useState(false);
  const { applyInactiveFilter, statusLookupsReady, showInactiveObjects } =
    useReportInactiveObjects();
  const tablePending = reportsLoading || !statusLookupsReady;

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);
  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const tableColumns = useMemo(() => WORKING_HOURS_FIRST_LAYER_COLUMNS(t), [t]);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const workingHoursFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    const startDate = new Date(dateValues.startDate);
    const endDate = new Date(dateValues.endDate);

    const sameDate = startDate.toDateString() === endDate.toDateString();

    if (sameDate) {
      startDate.setHours(0, 0, 0);
      endDate.setHours(23, 59, 59);
    }

    const formattedStartDate = convertToNormalTime(startDate);
    const formattedEndDate = convertToNormalTime(endDate, true);
    setFilterValues(values);

    const payload = {
      ...(apiPayload ?? {}),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      object: values.assignedToAllObjects ? [] : values.object,
      reportType: 3,
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(
        values.startTime + ":00",
        startDate,
        false
      ),
      endTime: reportTransformTimeForAPI(
        values.endTime + ":00",
        endDate,
        true,
        startDate
      )
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = useCallback((value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  }, []);

  const handleDateRangeSubmit = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };
    if (filterValues.daysOfWeek) {
      updatedFilterValues = {
        ...filterValues,
        daysOfWeek:
          filterValues.daysOfWeek.length === 7
            ? []
            : filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          filterValues.startTime + ":00",
          dateValues.startDate,
          false
        ),
        endTime: reportTransformTimeForAPI(
          filterValues.endTime + ":00",
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      };
    }

    const payload = {
      ...(apiPayload ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? []),
      reportType: 3
    };

    setApiPayload(payload);
  };

  const handleLoadReport = () => {
    const payload = buildReportFilterPayload({
      values: filterValues,
      dateValues,
      reportType: 3,
      normalizeDates: false,
      startDateOverride: convertToNormalTime(dateValues.startDate),
      endDateOverride: convertToNormalTime(dateValues.endDate, true)
    });

    setApiPayload(payload);
    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  const customStyles = {
    head: {
      style: {
        fontWeight: "900"
      }
    }
  };

  useEffect(() => {
    // dispatch(resetReports());
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  const formattedData = useMemo(
    () => applyInactiveFilter(formattedWorkingHours(localReport, dateFormat)),
    [localReport, dateFormat, applyInactiveFilter]
  );

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.workingHours")}
        disabled={reportsLoading ? true : false}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          disableExportButton={!formattedData?.length}
          reportType={3}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
          handleLoadReport={handleLoadReport}
          formattedData={formattedData}
        />
        <WorkingHoursFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={workingHoursFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            reportsWorkingHoursFormInitialValues
          )}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(reportsWorkingHoursFormInitialValues)
            );
          }}
        />
        <Table
          columns={tableColumns}
          data={formattedData}
          progressPending={tablePending}
          noDataComponent={!tablePending ? <ReportEmptyState /> : null}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={WorkingHoursDateRowCard}
          customStyles={customStyles}
          pagination={false}
          translator={t}
        />
      </Box>
    </Box>
  );
};

export default WorkingHours;
